import classNames from "classnames";
import Flickity from "flickity-imagesloaded";
import React from "react";
import { t } from "ttag";

// TODO(i18n): Verify that headline, descript, and label props come pre-translated from backend/CMS
export interface ISlideshowCell {
    thumbnailURL: string;
    thumbnail: string;
    headline: string;
    descript: string;
    label: string;
    styling: string;
}

const buildSlides = (images: ISlideshowCell[]): React.ReactNode => {
    return images.map((img) => {
        const slideStyle = {
            backgroundImage: `url(${img.thumbnailURL})`,
        };
        const slideClasses = classNames({
            "slideshow__cell": true,
            "slideshow__cell--inverse": img.styling !== "",
        });
        return (
            <div key={img.thumbnailURL} className={slideClasses}>
                <div className="slideshow__background" style={slideStyle}>
                    <div className="slideshow__content">
                        <div className="slideshow__copy">
                            <h3 className="slideshow__headline type_x1point5">
                                {img.headline}
                            </h3>
                            <div
                                className="slideshow__descript"
                                dangerouslySetInnerHTML={{
                                    __html: img.descript,
                                }}
                            ></div>
                        </div>
                        <div className="slideshow__copy-bg"></div>
                    </div>
                </div>
            </div>
        );
    });
};

const buildSlidesNav = (
    images: ISlideshowCell[],
    selectedIndex: number,
    onSelectSlideNav: (
        slideNum: number,
        event: React.MouseEvent<HTMLElement>,
    ) => void,
): React.ReactNode => {
    return images.map((img, i) => {
        const slideNavClasses = classNames({
            "slideshow__thumb-nav-item": true,
            "slideshow__thumb-nav-item--selected": i === selectedIndex,
        });
        return (
            <li key={img.thumbnailURL} className={slideNavClasses}>
                <button
                    onClick={(event) => onSelectSlideNav(i, event)}
                    title={img.headline}
                >
                    <img src={img.thumbnail} alt="" />
                    <span
                        className="slideshow__thumb-nav-label"
                        dangerouslySetInnerHTML={{ __html: img.label }}
                    ></span>
                </button>
            </li>
        );
    });
};

const initFlickity = (
    carousel: HTMLDivElement,
    setSelectedIndex: (index: number) => void,
): Flickity => {
    const flickity = new Flickity(carousel, {
        cellSelector: ".slideshow__cell",
        cellAlign: "left",
        contain: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        setGallerySize: false,
        imagesLoaded: true,
    });

    flickity.on("change", () => {
        setSelectedIndex(flickity.selectedIndex);
    });

    return flickity;
};

export const FlagshipSlideshow = (props: {
    images: ISlideshowCell[];
}): React.ReactNode => {
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    const slideshowCarousel = React.useRef<HTMLDivElement>(null);
    const slideshowFlkty = React.useRef<Flickity | null>(null);

    const onSelectSlideNav = (
        slideNum: number,
        event: React.MouseEvent<HTMLElement>,
    ) => {
        if (event) {
            event.preventDefault();
        }
        if (slideshowFlkty.current) {
            slideshowFlkty.current.select(slideNum);
        }
    };

    React.useEffect(() => {
        const carouselElem = slideshowCarousel.current;
        if (carouselElem) {
            slideshowFlkty.current = initFlickity(
                carouselElem,
                setSelectedIndex,
            );
        }

        return () => {
            if (slideshowFlkty.current) {
                slideshowFlkty.current.destroy();
                slideshowFlkty.current = null;
            }
        };
    }, []);

    return (
        <div className="slideshow slideshow--thumbs" ref={slideshowCarousel}>
            {buildSlides(props.images)}
            <div className="slideshow__thumb-nav u-list-unstyled">
                <span className="ada-screenreader-only">
                    {t`Activating this element will cause content in the slideshow to be updated`}
                </span>
                <ul>
                    {buildSlidesNav(
                        props.images,
                        selectedIndex,
                        onSelectSlideNav,
                    )}
                </ul>
            </div>
        </div>
    );
};
